import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import manageCardsServices from '@src/services/manageMyCards.services';

export const cardGetPagedList = createAsyncThunk(
  'cardGetPagedList',
  async ({ body, pageNumber, pageSize, sortParams }, { getState, dispatch, rejectWithValue }) => {
    try {
      const customerId = await getState()?.customer?.selectedCustomer?.customerId;
      const addressIdList = await getState()?.manageMyCards?.addressIdList;
      if (!customerId) {
        return rejectWithValue({});
      }

      let data = {
        customerId,
        vouId: body?.vouId ? body?.vouId : 1,
        ...body,
        ...(sortParams && { ...sortParams }),
      };

      if (!body?.addressIdList && addressIdList) {
        data = {
          ...data,
          addressIdList,
        };
      }

      const response = await manageCardsServices.cardGetPagedList({
        data,
        pageNumber: pageNumber || 1,
      });

      dispatch(
        setFilterObject({
          body,
          pageNumber,
          pageSize,
        }),
      );
      return response;
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const checkEacContactInfo = createAsyncThunk(
  'checkEacContactInfo',
  async (body, { rejectWithValue }) => {
    try {
      return await manageCardsServices.checkEacContactInfo(body);
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getCardsByNumberList = createAsyncThunk(
  'getCardsByNumberList',
  async ({ customerId, vouId, cardNumberList }, { rejectWithValue }) => {
    try {
      return await manageCardsServices.cardGetPagedList({
        data: { customerId, vouId, cardNumberList },
        pageNumber: 1,
        pageSize: cardNumberList.length,
      });
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const sendEacCodeSms = createAsyncThunk(
  'sendEacCodeSms',
  async (body, { rejectWithValue }) => {
    try {
      return await manageCardsServices.sendEacCodeSms(body);
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const sendEacCodeEmail = createAsyncThunk(
  'sendEacCodeEmail',
  async (body, { rejectWithValue }) => {
    try {
      return await manageCardsServices.sendEacCodeEmail(body);
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const processTypesGetPagedList = createAsyncThunk(
  'processTypesGetPagedList',
  async (body, { getState, rejectWithValue }) => {
    try {
      const statusId = await getState()?.manageMyCards?.filterObject?.body?.statusId;

      return await manageCardsServices.processTypesGetPagedList({
        type: statusId !== null ? statusId : undefined,
      });
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const cardsCancelList = createAsyncThunk(
  'cardsCancelList',
  async (body, { getState, rejectWithValue, dispatch }) => {
    try {
      const selectedCards = body?.selectedCards
        ? body.selectedCards
        : await getState()?.manageMyCards?.selectedCards;
      const data = {
        requestDtos: selectedCards.map((item) => {
          return {
            cardNumber: item?.cardNumber,
            id: body?.id,
          };
        }),
      };
      //cardNumber: selectedCards.map(item => item.cardNumber),
      //...body,
      dispatch(cardGetPagedList(getState()?.manageMyCards?.filterObject));
      return await manageCardsServices.cardsCancelList(data);
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const cardActivate = createAsyncThunk('cardActivate', async (body, { rejectWithValue }) => {
  try {
    return await manageCardsServices.cardActivate({ cardNumber: body });
  } catch (error) {
    return rejectWithValue(error?.data);
  }
});

export const employeesUpdateList = createAsyncThunk(
  'employeesUpdateList',
  async (body, { rejectWithValue }) => {
    try {
      return await manageCardsServices.employeesUpdateList(body);
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const employeesUpdateAddressList = createAsyncThunk(
  'employeesUpdateAddressList',
  async (body, { rejectWithValue }) => {
    try {
      return await manageCardsServices.employeesUpdateAddressList(body);
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const cancelRequestsDelete = createAsyncThunk(
  'cancelRequestsDelete',
  async (body, { rejectWithValue }) => {
    try {
      return await manageCardsServices.cancelRequestsDelete(body);
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const mobileUpdatePayments = createAsyncThunk(
  'mobileUpdatePayments',
  async (body, { rejectWithValue }) => {
    try {
      return await manageCardsServices.updateMobilPayments(body);
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const mobileUpdatePaymentsCollective = createAsyncThunk(
  'mobileUpdatePaymentsCollective',
  async (body, { rejectWithValue }) => {
    try {
      return await manageCardsServices.updateMobilPaymentCollective(body);
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

const initialState = {
  list: [],
  tableProperty: {
    currentPage: 1,
    page: 1,
    pageSize: 10,
    totalCount: 0,
  },
  filterObject: {
    body: {},
    pageNumber: 1,
    pageSize: 10,
  },
  selectedCards: [],
  addressIdList: [],
};

export const manageMyCardsSlice = createSlice({
  name: 'manageMyCards',
  initialState,
  reducers: {
    setFilterObject: (state, action) => {
      state.filterObject = action.payload;
    },
    setSelectedCards: (state, action) => {
      state.selectedCards = action.payload;
    },
    setAddressIdList: (state, action) => {
      state.addressIdList = action.payload;
    },
    clearAddressIdList: (state, action) => {
      state.addressIdList = [];
    },
    clearList: (state) => {
      state.list = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(cardGetPagedList.fulfilled, (state, action) => {
      const newItems = action?.payload?.data?.items || [];
      const pagedProperty = action?.payload?.data?.pagedProperty || state.tableProperty || initialState?.tableProperty;

      const shouldAppend = action?.meta?.arg?.append;
      if (shouldAppend) {
        state.list = (newItems || []).concat(state.list || []);
        state.tableProperty = pagedProperty;
      } else {
        state.list = newItems;
        state.tableProperty = pagedProperty;
      }
    });
    builder.addCase(cardGetPagedList.rejected, (state) => {
      state.list = initialState.list;
      state.tableProperty = initialState?.tableProperty;
    });
  },
});

export const { setFilterObject, setSelectedCards, setAddressIdList, clearAddressIdList, clearList } =
  manageMyCardsSlice.actions;
